import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, AlertTriangle, Mail, Trash2 } from "lucide-react";

export default function DeleteAccount() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const del = async () => {
    setBusy(true);
    try {
      await apiFetch("/account/delete", { method: "POST", body: JSON.stringify({ reason: reason.trim() }) });
      toast.success("Account scheduled for deletion. You can sign back in within 30 days to reactivate.");
      logout();
      navigate("/login");
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-lg px-4 py-8 sm:px-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="rounded-full p-1.5 hover:bg-secondary"><ArrowLeft className="h-5 w-5" /></button>
        <h1 className="font-display text-xl font-extrabold">Delete your account</h1>
      </div>

      {/* Publicly visible disclosure so Play / App Store reviewers can read the process without signing in */}
      <p className="mt-4 text-sm text-muted-foreground" data-testid="delete-intro">
        This page explains how to request deletion of your Venda Market account and associated data.
      </p>

      <div className="mt-4 rounded-2xl border border-border bg-secondary/40 p-4 text-sm">
        <h2 className="font-semibold">How to request deletion</h2>
        <ol className="mt-2 list-decimal space-y-1 pl-5 text-muted-foreground">
          <li>Sign in to the Venda Market app or website.</li>
          <li>Go to <span className="font-medium text-foreground">Profile → Delete account</span> (or open this page while signed in).</li>
          <li>Confirm and submit the request using the button below.</li>
        </ol>
        <p className="mt-3 text-muted-foreground">
          Can&apos;t sign in? Email us and we&apos;ll process your request manually.
        </p>
        <a href="mailto:support@vendamarket.com?subject=Account%20deletion%20request" className="mt-2 inline-flex items-center gap-2 font-semibold text-price" data-testid="delete-email">
          <Mail className="h-4 w-4" /> support@vendamarket.com
        </a>
      </div>

      <div className="mt-4 rounded-2xl border border-border bg-secondary/40 p-4 text-sm">
        <h2 className="font-semibold">What is deleted and retention</h2>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">
          <li>Deleted: your profile, account details, listings, and chat messages.</li>
          <li>Your account and listings are deactivated immediately on request.</li>
          <li>You have a <span className="font-medium text-foreground">30-day grace period</span> to reactivate by signing back in.</li>
          <li>After 30 days, your account and associated data are permanently removed.</li>
          <li>Limited records may be retained only where required by law (e.g. fraud prevention, legal obligations).</li>
        </ul>
      </div>

      {user ? (
        <>
          <div className="mt-6 flex gap-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            <AlertTriangle className="h-5 w-5 shrink-0" />
            <span>Your account and listings will be deactivated immediately. You have a 30-day grace period to reactivate by signing back in, after which everything is permanently removed.</span>
          </div>

          <div className="mt-4">
            <label className="mb-1.5 block text-sm font-medium">Reason (optional)</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="w-full rounded-xl border border-input p-3 text-sm outline-none focus:border-price" data-testid="delete-reason" />
          </div>

          <label className="mt-4 flex items-center gap-2 text-sm">
            <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} className="h-4 w-4 accent-red-600" data-testid="delete-confirm" />
            I understand my account will be deactivated.
          </label>

          <button onClick={del} disabled={!confirm || busy} className="mt-4 w-full rounded-xl bg-destructive py-3 text-sm font-semibold text-white disabled:opacity-50" data-testid="delete-account-btn">
            {busy ? "Deleting…" : "Delete my account"}
          </button>
        </>
      ) : (
        <button onClick={() => navigate("/login")} className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-destructive py-3 text-sm font-semibold text-white" data-testid="delete-signin-btn">
          <Trash2 className="h-4 w-4" /> Sign in to delete your account
        </button>
      )}
    </div>
  );
}
